import type { CalendarEventDto } from '../../../api/calendar';

/**
 * monthLayout (캘린더 상세화 2026-06-07 S5) — 월뷰 주(week) 단위 멀티데이 가로 바 배치.
 *
 * 한 주(7일)에 걸친 이벤트들을 레인(가로 줄)에 배치한다. 멀티데이/종일 이벤트는 시작열~종료열을
 * 차지하는 가로 바(isBar), 단일일 시간 이벤트는 한 열 칩. 겹치지 않게 greedy lane packing.
 * maxLanes 초과분은 열별 hidden 카운트(more[col])로 집계 — 셀의 "+N" 으로 노출.
 *
 * 순수 함수(Date/DOM 부수효과 없음, 입력 events 불변) — 단위 테스트 가능.
 */

export interface WeekSegment {
  event: CalendarEventDto;
  colStart: number;  // 0~6 (해당 주 내 시작 열, 클램프됨)
  colEnd: number;    // 0~6 (해당 주 내 종료 열, inclusive)
  lane: number;      // 0-based 레인
  isBar: boolean;    // true=가로 바(멀티데이/종일), false=단일 칩
  continuesLeft: boolean;   // 이벤트가 이 주 이전부터 이어짐(좌측 둥근모서리 X)
  continuesRight: boolean;  // 이벤트가 이 주 이후로 이어짐(우측 둥근모서리 X)
}

export interface WeekLayout {
  segments: WeekSegment[]; // lane < maxLanes 만
  more: number[];          // 길이 7, 열별 숨겨진(레인 초과) 이벤트 수
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** 로컬 자정 기준 day index(epoch day). */
function epochDay(d: Date): number {
  const local = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.floor(local.getTime() / DAY_MS - local.getTimezoneOffset() / (24 * 60));
}

/**
 * ⭐ **며칠 옮겼나** — 월간 드래그 이동(M-4 · 정본 그대로 · 2026-08-24 사용자 리포트 #39).
 *
 * 로컬 «달력일» 기준으로 센다 — `epochDay` 가 이미 자정으로 정규화하므로 **DST 등 시각
 * 오차와 무관**하다. 시각(time-of-day)은 그대로 유지된다.
 * ⇒ ★ 이 값에 **1440**(하루 분)을 곱해 `handleEventMove(deltaMinutes)` 에 그대로 넘긴다 —
 *   주간(`TimeGridView`)이 쓰는 그 함수와 **같은 꼴**이라 새로 짤 것이 없다.
 */
export function daysBetween(from: Date, to: Date): number {
  return epochDay(to) - epochDay(from);
}

/**
 * @param weekStart 주의 첫날(일요일) 00:00 로컬
 * @param events    전체 이벤트(주 범위 밖은 내부에서 제외)
 * @param maxLanes  표시 레인 상한(초과는 more 로)
 */
export function layoutWeek(weekStart: Date, events: CalendarEventDto[], maxLanes = 4): WeekLayout {
  const weekStartDay = epochDay(weekStart);
  const weekEndDay = weekStartDay + 6;

  // 1) 주에 걸친 이벤트 → 세그먼트(콜 범위) 산출.
  const raw: Array<Omit<WeekSegment, 'lane'>> = [];
  for (const e of events) {
    const s = new Date(e.startAt);
    const en = new Date(e.endAt);
    const startDay = epochDay(s);
    // 종료일: 종일/자정 종료의 off-by-one 방지 — endAt 가 자정(00:00)이면 직전일을 종료로.
    let endDay = epochDay(en);
    if (en.getHours() === 0 && en.getMinutes() === 0 && en.getSeconds() === 0 && endDay > startDay) {
      endDay -= 1;
    }
    if (endDay < startDay) endDay = startDay;
    // 주와 교집합 없으면 skip.
    if (endDay < weekStartDay || startDay > weekEndDay) continue;
    const colStart = Math.max(0, startDay - weekStartDay);
    const colEnd = Math.min(6, endDay - weekStartDay);
    const isBar = Boolean(e.isAllDay) || endDay > startDay;
    raw.push({
      event: e,
      colStart,
      colEnd,
      isBar,
      continuesLeft: startDay < weekStartDay,
      continuesRight: endDay > weekEndDay,
    });
  }

  // 2) 정렬: 바 먼저, 그다음 시작열 → 긴 span 우선 → 시작시각.
  raw.sort((a, b) => {
    if (a.isBar !== b.isBar) return a.isBar ? -1 : 1;
    if (a.colStart !== b.colStart) return a.colStart - b.colStart;
    const spanA = a.colEnd - a.colStart;
    const spanB = b.colEnd - b.colStart;
    if (spanA !== spanB) return spanB - spanA;
    return new Date(a.event.startAt).getTime() - new Date(b.event.startAt).getTime();
  });

  // 3) greedy lane packing — 각 레인은 점유 열(boolean[7]) 추적.
  const laneCols: boolean[][] = [];
  const segments: WeekSegment[] = [];
  const more = [0, 0, 0, 0, 0, 0, 0];

  for (const seg of raw) {
    let placed = -1;
    for (let lane = 0; lane < laneCols.length; lane++) {
      let free = true;
      for (let c = seg.colStart; c <= seg.colEnd; c++) {
        if (laneCols[lane]![c]) { free = false; break; }
      }
      if (free) { placed = lane; break; }
    }
    if (placed === -1) { placed = laneCols.length; laneCols.push([false, false, false, false, false, false, false]); }
    for (let c = seg.colStart; c <= seg.colEnd; c++) laneCols[placed]![c] = true;

    if (placed < maxLanes) {
      segments.push({ ...seg, lane: placed });
    } else {
      for (let c = seg.colStart; c <= seg.colEnd; c++) more[c]! += 1;
    }
  }

  return { segments, more };
}

/**
 * ⭐ 넘치는 날은 «마지막 줄 자리»를 「+n개」로 쓴다 (2026-10-19 사용자 결정).
 *
 * 종전에는 줄(레인) 4개를 다 그리고 「+n」을 그 «아래 5번째 줄»에 그려, 칸 높이(120px)를 넘어
 * 아래 8px 가 잘렸다. 이제 칸 높이·줄 수는 그대로 두고:
 *   - 그 날 일정이 줄 수(maxLanes) «이하»면 종전 그대로 다 그린다.
 *   - 넘치면 그 날은 앞의 maxLanes-1 줄만 그리고, 마지막 줄 자리를 「+n개」로 채운다
 *     (n = 그 날 일정 수 - (maxLanes - 1)).
 * 마지막 줄에 놓였던 여러 날짜 막대가 넘치는 날을 지나가면 그 날만 빼고 조각으로 나눈다
 * (넘치지 않는 날에서는 그대로 보인다 — 조각 끝은 「이어짐」 모양).
 * ⚠ `layoutWeek` 은 그대로 두고 그 결과를 고친다 — 종전 배치 규칙(정렬·레인 채우기)은 한 곳에만 있다.
 */
export function fitOverflowIntoLastLane(layout: WeekLayout, maxLanes = 4): WeekLayout {
  const last = maxLanes - 1;
  const total = [...layout.more];
  for (const s of layout.segments) for (let c = s.colStart; c <= s.colEnd; c++) total[c]! += 1;
  const over = total.map((n) => n > maxLanes);
  if (!over.some(Boolean)) return layout;

  const segments: WeekSegment[] = [];
  for (const s of layout.segments) {
    if (s.lane !== last) { segments.push(s); continue; }
    // 마지막 줄 조각 — 넘치는 날을 빼고 이어진 구간마다 나눈다.
    let runStart = -1;
    for (let c = s.colStart; c <= s.colEnd + 1; c++) {
      const keep = c <= s.colEnd && !over[c];
      if (keep && runStart === -1) runStart = c;
      if (!keep && runStart !== -1) {
        segments.push({
          ...s,
          colStart: runStart,
          colEnd: c - 1,
          continuesLeft: runStart > s.colStart || s.continuesLeft,
          continuesRight: c - 1 < s.colEnd || s.continuesRight,
        });
        runStart = -1;
      }
    }
  }
  const more = total.map((n, c) => (over[c] ? n - last : 0));
  return { segments, more };
}
